import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getTenantId, applyTenantTheme, isLandingPage, getSubdomain } from "@/utils/tenant";
import { tenantApi, TenantInfo } from "@/api/tenant";
import i18n from '@/i18n'

// 앱 시작 시 잘못된 데이터 정리
const cleanupTenant = () => {
    if (typeof window === "undefined") return;

    const hostname = window.location.hostname;
    const subdomain = getSubdomain(hostname);

    // 서브도메인이 변경되었으면 이전 인증 정보 삭제
    const storedTenant = localStorage.getItem("tenant");
    if (subdomain && storedTenant !== subdomain) {
        console.warn(`[Tenant] Subdomain changed to '${subdomain}'. Clearing auth.`);
        localStorage.setItem("tenant", subdomain);
        localStorage.removeItem("auth-storage");
        localStorage.removeItem("tenant-storage");
    }
    
    // 랜딩 페이지에서는 기존 tenant 정보 유지하지 않음
    if (!subdomain) {
        const stored = localStorage.getItem("tenant");
        if (stored && stored !== "null") {
            console.log(`[Tenant] Landing page - keeping stored tenant for reference: ${stored}`);
        }
    }
};

// 모듈 로드 시 자동 실행
cleanupTenant();

interface TenantState {
    tenantId: string | null;
    tenantInfo: TenantInfo | null;
    isLoading: boolean;
    error: string | null;
    isLanding: boolean;

    // Actions
    initializeTenant: () => Promise<void>;
    setTenantInfo: (info: TenantInfo) => void;
    clearTenant: () => void;
}

export const useTenantStore = create<TenantState>()(
    persist(
        (set) => ({
            tenantId: null,
            tenantInfo: null,
            isLoading: true,
            error: null,
            isLanding: false,

            initializeTenant: async () => {
                // 랜딩 페이지 확인
                if (isLandingPage()) {
                    console.log("[Tenant] Landing page mode - no tenant");
                    set({ tenantId: null, tenantInfo: null, isLoading: false, isLanding: true });
                    return;
                }

                // 서브도메인 기반 테넌트 식별
                const tenantId = getTenantId();

                if (!tenantId) {
                    set({ tenantId: null, tenantInfo: null, isLoading: false, isLanding: true });
                    return;
                }

                // 무조건 로딩 상태로 시작
                set({ tenantId, isLoading: true, error: null, isLanding: false });

                try {
                    console.log(`[Tenant] Fetching info for: ${tenantId}`);
                    const response = await tenantApi.getTenantBySubdomain(tenantId);

                    if (response.data.success && response.data.data) {
                        const info = response.data.data;
                        console.log(`[Tenant] Loaded info: ${info.name}`);
                        set({ tenantInfo: info, isLoading: false });
                        applyTenantTheme(info.primaryColor);
                        // 조직 언어 설정 적용
                        const langMode = info.languageMode || 'ko'
                        if (langMode === 'ko' || langMode === 'en') {
                            // 단일 언어 모드: 강제 설정
                            i18n.changeLanguage(langMode)
                        } else if (langMode === 'both') {
                            // 양쪽 다 지원: 저장된 사용자 선택 유지 (localStorage의 i18nextLng)
                            // 별도 처리 불필요 - i18n이 localStorage에서 자동 복원
                        }
                    } else {
                        throw new Error("Tenant data missing in response");
                    }
                } catch (error) {
                    console.error("[Tenant] Load failed:", error);
                    set({
                        tenantInfo: null,
                        isLoading: false,
                        error: "Failed to load tenant",
                    });
                }
            },

            setTenantInfo: (info) => {
                set({ tenantInfo: info });
                applyTenantTheme(info.primaryColor);
            },

            clearTenant: () => {
                set({ tenantId: null, tenantInfo: null });
                applyTenantTheme(null);
            },
        }),
        {
            name: "tenant-storage",
            partialize: (state) => ({
                // tenantId도 저장하지 않음 - 항상 서브도메인에서 계산
                tenantInfo: state.tenantInfo,
            }),
        }
    )
);
